import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

import { HttpClient, HttpHeaders, HttpClientModule } from '@angular/common/http';
import { Logger } from '../logger.service';
import { JwtHelperService } from '@auth0/angular-jwt';

const serviceKey: string = 'Authentication';
const log = new Logger('IDMS: ' + serviceKey);

export interface Context {
  idms: string;
}

const contextKey = 'context';
const codeKey = 'code';
const urlsKey = 'urls';
const tokenKey = 'token';
const environment = (window as any).__env
@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  // private _context: Context | null;
  private _context;

  constructor(
    private readonly http: HttpClient,
    private readonly _http: HttpClient,
    private readonly router: Router, public jwtHelper: JwtHelperService
  ) {
    const text: string | null =
      sessionStorage.getItem(contextKey) || localStorage.getItem(contextKey);
    this._context = text && JSON.parse(text);
  }

  /** Authenticates the user. stores context(user info, token info), idms urls and code which is provided after login */
  login(_remember?) {
    const code: string = sessionStorage.getItem(codeKey);
    if (!sessionStorage.getItem(urlsKey) || !sessionStorage.getItem(codeKey)) {
      this.getIdmsUrls().subscribe((response) => {
        sessionStorage.setItem(urlsKey, JSON.stringify(response));
        if (!sessionStorage.getItem(codeKey)) {
          // Redirect to login with default language a English always
          window.location.href = response.loginEndPoint + '&lang=' + environment.defaultLanguage;
        }
      });
    }

    if (!!sessionStorage.getItem(codeKey)) {
      // if logged in set the session storage
      //console.log('Token receiving...');
      this.getContext(code) // Get User and Token Detail
      .subscribe((response) => {
          sessionStorage.setItem(tokenKey, response.toString().replace('"', ''));
          const context = { idms: this.ParseJwt(response.toString()) };
          this.login_idms(context).subscribe(c => {
            if (c.idms) {
              this.navigateToPage('');
            }
          });
        });
    }
  }

  /* clears the session storage and logs out the user. */
  logout() {
    const logout: string = JSON.parse(this.urls).logout;
    this.setContext();
    window.location.href = logout;
    return throwError('');
  }

  validateLogin(url: string): boolean {
    if (this.isAuthenticated()) {
        return true;
    } else {
       this.validateLogin_noCode(url);
    }
    return false;
  }

  validateLogin_noCode(url: string) {
    if (!sessionStorage.getItem('code')) {
      const queryParam = decodeURIComponent(window.location.search.substring(1));
      const code: string = queryParam.split('&')[0].split('code=')[1];
      const isLogin: boolean = queryParam.split('&')[0].split('login=')[1] === 'true' ? true : false;
      const autologin = window.location.pathname === '/autologin' ? true : false;

      if (code !== undefined) {
        sessionStorage.setItem(codeKey, code);
      } else if (url === '/home' && !autologin && !isLogin) {
        // Return true if its home page
        return true;
      } else {
        //sonarFix
      }
      this.navigateToPage('oauth');
    } else {
      this.login();
    }
    return false;
  }
  
  /*** Authenticates the user.
     * @param {Context} context The login parameters.
     * @return {Observable<Context>} The user credentials.
     */
  private login_idms(context: Context, remember?): Observable<Context> {
    this.setContext(context, remember);
    return of(context);
  }

  /*Properties Exposed*/
    /**
     * Checks is the user is authenticated.
     * @return {boolean} True if the user is authenticated.
     */
  isAuthenticated(): boolean {
    return !!this.context;
  }

  /*** Gets the user context.
     * @return {Context} The user context(user info, token info) or null if the user is not authenticated.
     */
    get context(): Context | null {
      return this._context;
  }

  /** Gets the user logged in user name */
  get authenticatedUser(): string {
      return JSON.parse(JSON.stringify(this._context)).idms.name;
  }

  /** Gets the user logged in user email */
  get authenticatedUserEmail(): string {
      return JSON.parse(JSON.stringify(this._context)).idms.email;
  }

  get authenticatedUserId(): string {
      return JSON.parse(JSON.stringify(this._context)).idms.userid;
  }

  get authenticatedRoleCd(): string {
      return JSON.parse(JSON.stringify(this._context)).idms.roleCode;
  }

  get adminRole(): string {
    return JSON.parse(JSON.stringify(this._context)).idms.isAdmin;
  }

  /** Gets the idms url list */
  get urls() {
      return JSON.parse(JSON.stringify(sessionStorage.getItem(urlsKey)));
  }

  /**
   * Sets the user context.
   * The context may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param {Context=} context The user information.
   * @param {boolean=} remember True to remember context across sessions.
   */
    public setContext(context?: Context, remember?) {
      this._context = context || null;

      if (context) {
          const storage = remember ? localStorage : sessionStorage;
          storage.setItem(contextKey, JSON.stringify(context));
      } else {
          sessionStorage.removeItem(contextKey);
          localStorage.removeItem(contextKey);
          sessionStorage.removeItem(codeKey);
          sessionStorage.removeItem(urlsKey);
      }
  }

  /**
   * Gets the user information from Industry API.
   * @param {code=} code returned from IDMS login page.
   * @return {Context=} context which contains (Logged in User information and logged in user token details).
   */

  private getContext(code: string) {
    return this.http.get(environment.serverAuth + '/api/application/' + code + '/accesstoken', {responseType: 'text'} )
    // .pipe(map((res: Response) => res.text().toString()));
  }

  public ParseJwt(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace('-', '+').replace('_', '/');
    return JSON.parse(window.atob(base64));
  }

  /**
   * Gets the Urls from Industry API.
   * @return {Urls=} urls.
   */

  private getIdmsUrls(): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders();
    headers.append('redirectURL', environment.redirect_uri);
    return this.http.get(environment.serverAuth + '/api/application/GetLoginUrls', {headers});
  }

  get token(): string {
    const token = sessionStorage.getItem('token').replace('"', '');
    if (token === '') {
        log.error('token not available... login again');
    }
    return token;
  }

  public navigateToPage(page: string) {
    this.router.navigate(['/' + page]);
  }
   /**
   *  API call for refresh token
   */
  public refreshToken(): Observable<any> {
    const expiredToken = sessionStorage.getItem('token');
    if (expiredToken !== null && this._context) {
      const refreshToken = this._context.idms.refreshToken;

      const headers: HttpHeaders = new HttpHeaders();
      headers.set('accessToken', expiredToken);

      return this.http
      .get(environment.serverAuth + '/api/application/refreshToken/' + refreshToken + '/accesstoken', {headers})
        .pipe(
          map((response) => {
            return response;
          }),
          catchError((error) => {
            return throwError(error);
          })
        );
    }
    return throwError('');
  }

}
